import { Component, OnInit } from '@angular/core';
import { CrudService } from '../service/crud.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedService } from '../service/shared.service';
import { AuthService } from '../service/auth.service';
import { ECR } from '../models/ecr';
import { ECRService } from '../service/ecr.service';
import { TravelService } from '../service/travel.service';
import { TourServiceService } from '../service/tour-service.service';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface DashboardLink {
  label: string;
  route: string;
  icon: string;
  requireReportees?: boolean;
  requireDriverManagerInbox?: boolean;
}

export interface DashboardModule {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  theme: string;
  links: DashboardLink[];
}

export interface ModuleStatCounts {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  draft: number;
}

export interface GlanceKpi {
  id: string;
  label: string;
  value: number;
  sublabel: string;
  icon: string;
  tone: string;
  route?: string;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit {
  tmc: any;
  token: any = '';
  CompanyName: any = '';
  EcrModel: ECR = new ECR();
  Reportees: any = '';
  Band: any = '';
  userName: string = '';
  userDesignation: string = '';
  isRefreshing = false;
  statsReady = false;
  glanceEnterPlayed = false;
  glanceKpis: GlanceKpi[] = [];

  private loadGeneration = 0;
  private readonly CACHE_KEY = 'dashboardStatsCache';
  private readonly CACHE_TTL_MS = 3 * 60 * 1000;
  private loadedFromUnified = {
    ecr: false,
    ecrMonth: false,
    localTravel: false,
    tour: false,
    travelDesk: false,
    driver: false,
    pending: false
  };
  private unifiedPendingTotal: number | null = null;

  ecrStats: ModuleStatCounts = this.emptyStats();
  ecrThisMonth = 0;
  localTravelStats: ModuleStatCounts = this.emptyStats();
  tourStats: ModuleStatCounts = this.emptyStats();
  travelDeskStats = { total: 0, pending: 0, rejected: 0, closed: 0 };
  driverStats: ModuleStatCounts = this.emptyStats();

  private readonly ecrModule: DashboardModule = {
    id: 'ecr',
    title: 'ECR',
    subtitle: 'Executive Contribution Report',
    icon: 'las la-clipboard-list',
    theme: 'theme-ecr',
    links: [
      { label: 'Fill ECR', route: '/ecr', icon: 'las la-edit' },
      { label: 'ECR Status (Self)', route: '/ecr-status', icon: 'las la-tasks' },
      { label: 'View Team Members ECR', route: '/view-ecr-reportees', icon: 'las la-users', requireReportees: true }
    ]
  };

  private readonly travelClaimModule: DashboardModule = {
    id: 'travel-claim',
    title: 'Tour & Local Travel Claim',
    subtitle: 'Submit and track travel claims',
    icon: 'las la-route',
    theme: 'theme-travel',
    links: [
      { label: 'Local Travel Claim Requisition', route: '/travel/travelreq', icon: 'las la-car-side' },
      { label: 'Tour Travel Requisition', route: '/tour/tourclaimreq', icon: 'las la-plane-departure' },
      { label: 'Local Claim Status', route: '/travel/travelstatus', icon: 'las la-list-alt' },
    ]
  };

  private readonly travelRequestModule: DashboardModule = {
    id: 'travel-request',
    title: 'Travel Request',
    subtitle: 'Official travel desk requests',
    icon: 'las la-suitcase-rolling',
    theme: 'theme-desk',
    links: [
      { label: 'Raise Travel Request', route: '/traveldesk/travelRequest', icon: 'las la-plus-circle' },
      { label: 'Travel Dashboard', route: '/traveldesk/DashboardTravel', icon: 'las la-th-large' },
      { label: 'Travel Status (Self)', route: '/traveldesk/TraveldeskStatus', icon: 'las la-eye' }
    ]
  };

  private readonly driverModule: DashboardModule = {
    id: 'driver',
    title: 'Driver Reimbursement',
    subtitle: 'Driver expense claims',
    icon: 'las la-id-card',
    theme: 'theme-driver',
    links: [
      { label: 'Raise Reimbursement Request', route: '/driverreimbursement/driver-requisition', icon: 'las la-file-invoice' },
      { label: 'Reimbursement Status', route: '/driverreimbursement/driver-view', icon: 'las la-check-double' },
      { label: 'Manager Approval Inbox', route: '/driverreimbursement/manager-view', icon: 'las la-inbox', requireDriverManagerInbox: true }
    ]
  };

  constructor(
    private _crudService: CrudService,
    private _ecrService: ECRService,
    private _travelService: TravelService,
    private _tourService: TourServiceService,
    private activatedRoute: ActivatedRoute,
    private sharedService: SharedService,
    private authService: AuthService,
    private _router: Router
  ) {
    this.tmc = sessionStorage.getItem('userId');
    this.EcrModel.TMCNo = sessionStorage.getItem('userId');
    this.EcrModel.EmployeeName = sessionStorage.getItem('FullName');
    this.EcrModel.FromEmail = sessionStorage.getItem('UserEmail');
    this.CompanyName = sessionStorage.getItem('CompanyName');
    this.Reportees = sessionStorage.getItem('Reportees');
    this.Band = sessionStorage.getItem('Band');
    this.userName = sessionStorage.getItem('FullName') || '';
    this.userDesignation = sessionStorage.getItem('UserDesignation') || '';
    this.token = this.activatedRoute.snapshot.queryParamMap.get('t');
  }

  ngOnInit() {
    this.refreshSessionContext();
    this.rebuildGlanceKpis();
    if (this.token) {
      this.loginFromSSO();
    } else if (!sessionStorage.getItem('FullName')) {
      this._router.navigate(['/']);
    } else {
      this.loadDashboardSummary();
    }
  }

  private refreshSessionContext(): void {
    this.tmc = sessionStorage.getItem('userId');
    this.Band = sessionStorage.getItem('Band');
    this.Reportees = sessionStorage.getItem('Reportees');
    this.CompanyName = sessionStorage.getItem('CompanyName');
    this.userName = sessionStorage.getItem('FullName') || '';
    this.userDesignation = sessionStorage.getItem('UserDesignation') || '';
  }

  get greetingText(): string {
    const hour = new Date().getHours();
    if (hour < 12) {
      return 'Good morning';
    }
    if (hour < 17) {
      return 'Good afternoon';
    }
    return 'Good evening';
  }

  get userInitials(): string {
    if (!this.userName) {
      return 'U';
    }
    const parts = this.userName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return this.userName.substring(0, 2).toUpperCase();
  }

  get showDriverModule(): boolean {
    const tmc = (this.tmc || '').toString().trim();
    return this.Band === 'G3' || this.Band === 'G1' || this.Band === 'G2' || this.Band === 'G4'
      || tmc === '16268';
  }

  get dashboardModules(): DashboardModule[] {
    const modules = [this.ecrModule, this.travelClaimModule, this.travelRequestModule];
    if (this.showDriverModule) {
      modules.push(this.driverModule);
    }
    return modules;
  }

  get totalPendingActions(): number {
    if (this.unifiedPendingTotal !== null) {
      return this.unifiedPendingTotal;
    }
    return this.ecrStats.pending
      + this.localTravelStats.pending
      + this.tourStats.pending
      + this.travelDeskStats.pending
      + this.driverStats.pending;
  }

  trackByGlanceKpi(_index: number, kpi: GlanceKpi): string {
    return kpi.id;
  }

  private rebuildGlanceKpis(): void {
    const kpis: GlanceKpi[] = [
      {
        id: 'ecr',
        label: 'ECR Submitted',
        value: this.ecrThisMonth,
        sublabel: 'This month',
        icon: 'las la-clipboard-list',
        tone: 'tone-teal',
        route: '/ecr-status'
      },
      {
        id: 'local',
        label: 'Local Travel',
        value: this.localTravelStats.total,
        sublabel: '',
        icon: 'las la-car-side',
        tone: 'tone-mint',
        route: '/travel/travelstatus'
      },
      {
        id: 'tour',
        label: 'Tour Travel',
        value: this.tourStats.total,
        sublabel: '',
        icon: 'las la-plane-departure',
        tone: 'tone-lime',
        route: '/tour/tourclaimstatus'
      },
      {
        id: 'desk',
        label: 'Travel Requests',
        value: this.travelDeskStats.total,
        sublabel: ' ',
        icon: 'las la-suitcase-rolling',
        tone: 'tone-gold',
        route: '/traveldesk/TraveldeskStatus'
      },
      {
        id: 'pending',
        label: 'Pending Actions',
        value: this.totalPendingActions,
        sublabel: '',
        icon: 'las la-hourglass-half',
        tone: 'tone-alert'
      }
    ];

    this.glanceKpis = kpis;
  }

  showLink(link: DashboardLink): boolean {
    if (link.requireDriverManagerInbox) {
      return this.sharedService.isDriverManagerApprovalUser(this.tmc);
    }
    return !link.requireReportees || this.Reportees !== '';
  }

  getModuleIndex(index: number): string {
    const value = index + 1;
    return value < 10 ? '0' + value : String(value);
  }

  getVisibleLinkCount(module: DashboardModule): number {
    return module.links.filter(link => this.showLink(link)).length;
  }

  refreshDashboard(): void {
    sessionStorage.removeItem(this.CACHE_KEY);
    this.glanceEnterPlayed = false;
    this.loadDashboardSummary(true);
  }

  loadDashboardSummary(forceRefresh: boolean = false): void {
    this.refreshSessionContext();
    if (!this.tmc) {
      return;
    }

    const generation = ++this.loadGeneration;
    this.isRefreshing = true;
    this.resetUnifiedFlags();

    if (!forceRefresh && this.loadFromCache()) {
      this.rebuildGlanceKpis();
      this.statsReady = true;
      this.isRefreshing = false;
      this.scheduleGlanceEntranceComplete();
      return;
    }

    this.statsReady = false;
    this.glanceEnterPlayed = false;
    this.unifiedPendingTotal = null;
    this.loadAllStats(generation);
  }

  private resetUnifiedFlags(): void {
    this.loadedFromUnified = {
      ecr: false,
      ecrMonth: false,
      localTravel: false,
      tour: false,
      travelDesk: false,
      driver: false,
      pending: false
    };
    this.unifiedPendingTotal = null;
  }

  /** Fast glance APIs first; slow list APIs only as background fallbacks */
  private loadAllStats(generation: number): void {
    const userId = this.tmc;

    this._crudService.get(`Profile/GetDashboardData?tmcid=${userId}`)
      .pipe(catchError(err => this.handleApiError('Unified dashboard', err)))
      .subscribe((unified) => {
        if (generation !== this.loadGeneration) {
          return;
        }
        this.applyUnifiedDashboardData(unified);
        this.loadFastGlanceSupplements(generation, userId);
      });
  }

  /** Small count/summary endpoints only — never wait on full travel/tour lists */
  private loadFastGlanceSupplements(generation: number, userId: any): void {
    const currentMonth = new Date().getMonth() + 1;
    const jobs: Array<{ key: string; request: Observable<any> }> = [];

    if (!this.loadedFromUnified.travelDesk) {
      jobs.push({
        key: 'desk',
        request: this._crudService.get(`TravelModule/GetVendorDashboardCount?id=${userId}`)
          .pipe(catchError(err => this.handleApiError('Travel desk', err)))
      });
    }
    if (!this.loadedFromUnified.ecrMonth) {
      jobs.push({
        key: 'ecrMonth',
        request: this._ecrService.GetECRByTMCAndMonth(userId, currentMonth)
          .pipe(catchError(err => this.handleApiError('ECR (month)', err)))
      });
    }

    if (jobs.length === 0) {
      this.finishGlanceDisplay(generation);
      this.loadGlanceFallbacks(generation, userId);
      return;
    }

    forkJoin(jobs.map(job => job.request)).subscribe((results) => {
      if (generation !== this.loadGeneration) {
        return;
      }
      for (let i = 0; i < jobs.length; i++) {
        const result = results[i];
        if (jobs[i].key === 'desk') {
          this.applyTravelDeskStats(this.extractTravelDeskCount(result));
        } else if (jobs[i].key === 'ecrMonth') {
          this.ecrThisMonth = this.extractList(result).length;
        }
      }
      this.finishGlanceDisplay(generation);
      this.loadGlanceFallbacks(generation, userId);
    });
  }

  /** Heavy list APIs — update values in place without re-showing skeleton */
  private loadGlanceFallbacks(generation: number, userId: any): void {
    const jobs: Array<{ key: string; request: Observable<any> }> = [];

    if (!this.loadedFromUnified.localTravel) {
      jobs.push({
        key: 'local',
        request: this._travelService.getTravelList(userId)
          .pipe(catchError(err => this.handleApiError('Local travel', err)))
      });
    }
    if (!this.loadedFromUnified.tour) {
      jobs.push({
        key: 'tour',
        request: this._tourService.getTourList(userId)
          .pipe(catchError(err => this.handleApiError('Tour travel', err)))
      });
    }
    if (this.showDriverModule && !this.loadedFromUnified.driver) {
      jobs.push({
        key: 'driver',
        request: this._crudService.get(`Reimbursement/GetAllClaims?userId=${userId}`)
          .pipe(catchError(err => this.handleApiError('Driver claims', err)))
      });
    }

    if (jobs.length === 0) {
      return;
    }

    forkJoin(jobs.map(job => job.request)).subscribe((results) => {
      if (generation !== this.loadGeneration) {
        return;
      }
      for (let i = 0; i < jobs.length; i++) {
        const result = results[i];
        if (jobs[i].key === 'local') {
          this.applyClaimStats(this.extractList(result), 'local');
        } else if (jobs[i].key === 'tour') {
          this.applyClaimStats(this.extractList(result), 'tour');
        } else if (jobs[i].key === 'driver') {
          this.applyDriverStats(this.extractList(result));
        }
      }
      this.updateGlanceValues();
      this.saveCache();
    });
  }

  private finishGlanceDisplay(generation: number): void {
    if (generation !== this.loadGeneration) {
      return;
    }
    this.rebuildGlanceKpis();
    this.statsReady = true;
    this.isRefreshing = false;
    this.scheduleGlanceEntranceComplete();
    this.saveCache();
  }

  private scheduleGlanceEntranceComplete(): void {
    if (this.glanceEnterPlayed) {
      return;
    }
    setTimeout(() => {
      this.glanceEnterPlayed = true;
    }, 900);
  }

  private updateGlanceValues(): void {
    this.rebuildGlanceKpis();
  }

  private loadFromCache(): boolean {
    try {
      const raw = sessionStorage.getItem(this.CACHE_KEY);
      if (!raw) {
        return false;
      }
      const cache = JSON.parse(raw);
      if (!cache || cache.userId !== this.tmc || Date.now() - cache.ts > this.CACHE_TTL_MS) {
        return false;
      }
      this.ecrStats = cache.ecrStats || this.emptyStats();
      this.ecrThisMonth = cache.ecrThisMonth || 0;
      this.localTravelStats = cache.localTravelStats || this.emptyStats();
      this.tourStats = cache.tourStats || this.emptyStats();
      this.travelDeskStats = cache.travelDeskStats || { total: 0, pending: 0, rejected: 0, closed: 0 };
      this.driverStats = cache.driverStats || this.emptyStats();
      this.unifiedPendingTotal = cache.unifiedPendingTotal !== undefined ? cache.unifiedPendingTotal : null;
      this.loadedFromUnified.pending = this.unifiedPendingTotal !== null;
      return true;
    } catch (e) {
      return false;
    }
  }

  private saveCache(): void {
    try {
      sessionStorage.setItem(this.CACHE_KEY, JSON.stringify({
        userId: this.tmc,
        ts: Date.now(),
        ecrStats: this.ecrStats,
        ecrThisMonth: this.ecrThisMonth,
        localTravelStats: this.localTravelStats,
        tourStats: this.tourStats,
        travelDeskStats: this.travelDeskStats,
        driverStats: this.driverStats,
        unifiedPendingTotal: this.unifiedPendingTotal
      }));
    } catch (e) {
      // Ignore quota errors
    }
  }

  /** Map Profile/GetDashboardData if backend returns summary counts */
  private applyUnifiedDashboardData(response: any): boolean {
    const row = this.extractDashboardRow(response);
    if (!row) {
      return false;
    }

    let mapped = 0;
    const num = (...keys: string[]) => this.readNumber(row, keys);

    const ecrTotal = num('TotalECR', 'ECRCount', 'TotalEcr', 'ECRSubmitted', 'EcrCount');
    const ecrMonth = num('ECRThisMonth', 'ECRMonthCount', 'ThisMonthECR');
    if (ecrTotal !== null) {
      this.ecrStats.total = ecrTotal;
      this.ecrStats.approved = ecrTotal;
      this.loadedFromUnified.ecr = true;
      mapped++;
    }
    if (ecrMonth !== null) {
      this.ecrThisMonth = ecrMonth;
      this.loadedFromUnified.ecrMonth = true;
      mapped++;
    }

    const localTotal = num('LocalTravelTotal', 'LocalTravelCount', 'TotalLocalTravel');
    const localPending = num('LocalTravelPending', 'LocalPending');
    const localApproved = num('LocalTravelApproved', 'LocalApproved');
    const localRejected = num('LocalTravelRejected', 'LocalRejected');
    if (localTotal !== null) {
      this.localTravelStats = {
        total: localTotal,
        pending: localPending || 0,
        approved: localApproved || 0,
        rejected: localRejected || 0,
        draft: num('LocalTravelDraft', 'LocalDraft') || 0
      };
      this.loadedFromUnified.localTravel = true;
      mapped++;
    }

    const tourTotal = num(
      'TourTravelTotal', 'TourClaimTotal', 'TotalTourTravel', 'TourTotal',
      'TotalTour', 'TourCount', 'TotalTourClaims', 'Tour_Claim_Count', 'TOUR_COUNT'
    );
    const tourPending = num(
      'TourTravelPending', 'TourPending', 'TourClaimPending', 'PendingTourTravel'
    );
    const tourApproved = num('TourTravelApproved', 'TourApproved');
    const tourRejected = num('TourTravelRejected', 'TourRejected');
    if (tourTotal !== null) {
      this.tourStats = {
        total: tourTotal,
        pending: tourPending || 0,
        approved: tourApproved || 0,
        rejected: tourRejected || 0,
        draft: num('TourTravelDraft', 'TourDraft') || 0
      };
      this.loadedFromUnified.tour = true;
      mapped++;
    }

    const deskTotal = num('TotalRequests', 'TravelRequestTotal');
    if (deskTotal !== null && !this.loadedFromUnified.travelDesk) {
      this.travelDeskStats = {
        total: deskTotal,
        pending: num('PendingForApproval', 'TravelRequestPending') || 0,
        rejected: num('RejectedRequest', 'TravelRequestRejected') || 0,
        closed: num('RequestClosed', 'TravelRequestClosed') || 0
      };
      this.loadedFromUnified.travelDesk = true;
      mapped++;
    }

    const driverTotal = num('DriverClaimTotal', 'DriverReimbursementTotal');
    if (driverTotal !== null) {
      this.driverStats = {
        total: driverTotal,
        pending: num('DriverClaimPending', 'DriverPending') || 0,
        approved: num('DriverClaimApproved', 'DriverApproved') || 0,
        rejected: num('DriverClaimRejected', 'DriverRejected') || 0,
        draft: num('DriverClaimDraft', 'DriverDraft') || 0
      };
      this.loadedFromUnified.driver = true;
      mapped++;
    }

    const pendingTotal = num(
      'TotalPendingActions', 'PendingActions', 'TotalPending', 'PendingCount',
      'PendingActionCount', 'TotalPendingAction'
    );
    if (pendingTotal !== null) {
      this.unifiedPendingTotal = pendingTotal;
      this.loadedFromUnified.pending = true;
      mapped++;
    }

    return mapped > 0;
  }

  private extractDashboardRow(response: any): any {
    if (!response) {
      return null;
    }
    if (Array.isArray(response) && response.length > 0) {
      return response[0];
    }
    if (response.Data && response.Data.length > 0) {
      return response.Data[0];
    }
    if (typeof response === 'object' && !Array.isArray(response)) {
      return response;
    }
    return null;
  }

  private readNumber(row: any, keys: string[]): number | null {
    for (let i = 0; i < keys.length; i++) {
      const value = row[keys[i]];
      if (value !== undefined && value !== null && value !== '') {
        const parsed = Number(value);
        return isNaN(parsed) ? 0 : parsed;
      }
    }
    const rowKeys = Object.keys(row);
    for (let k = 0; k < keys.length; k++) {
      const target = keys[k].toLowerCase();
      for (let r = 0; r < rowKeys.length; r++) {
        if (rowKeys[r].toLowerCase() === target) {
          const value = row[rowKeys[r]];
          if (value !== undefined && value !== null && value !== '') {
            const parsed = Number(value);
            return isNaN(parsed) ? 0 : parsed;
          }
        }
      }
    }
    return null;
  }

  private handleApiError(label: string, err: any) {
    console.warn(`Dashboard API [${label}] failed:`, err);
    return of(null);
  }

  /** Normalise API payloads — handles { Data }, { Status, Data }, or raw arrays */
  private extractList(response: any): any[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response.Data)) {
      return response.Data;
    }
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return [];
  }

  private extractTravelDeskCount(response: any): any {
    if (!response) {
      return {};
    }
    if (response.TotalRequests !== undefined || response.PendingForApproval !== undefined) {
      return response;
    }
    if (response.Data && response.Data.length > 0) {
      return response.Data[0];
    }
    if (Array.isArray(response) && response.length > 0) {
      return response[0];
    }
    return {};
  }

  private emptyStats(): ModuleStatCounts {
    return { total: 0, pending: 0, approved: 0, rejected: 0, draft: 0 };
  }

  private applyClaimStats(list: any[], type: 'local' | 'tour'): void {
    const stats = this.emptyStats();
    stats.total = list.length;
    list.forEach(item => {
      const status = item.STATUS || '';
      if (status === this.sharedService.DraftStatus) {
        stats.draft++;
      } else if (this.isApprovedStatus(status)) {
        stats.approved++;
      } else if (this.isRejectedStatus(status)) {
        stats.rejected++;
      } else if (this.isPendingStatus(status)) {
        stats.pending++;
      }
    });

    if (type === 'local') {
      this.localTravelStats = stats;
    } else {
      this.tourStats = stats;
    }
  }

  private applyTravelDeskStats(data: any): void {
    this.travelDeskStats = {
      total: Number(data.TotalRequests) || 0,
      pending: Number(data.PendingForApproval) || 0,
      rejected: Number(data.RejectedRequest) || 0,
      closed: Number(data.RequestClosed) || 0
    };
  }

  private applyDriverStats(list: any[]): void {
    const stats = this.emptyStats();
    const claims = Array.isArray(list) ? list : [];
    stats.total = claims.length;
    claims.forEach(item => {
      const status = (item.STATUS || '').toUpperCase();
      if (status === this.sharedService.DraftStatus) {
        stats.draft++;
      } else if (status.includes('PENDING')) {
        stats.pending++;
      } else if (status.includes('APPROVED')) {
        stats.approved++;
      } else if (status.includes('REJECT')) {
        stats.rejected++;
      }
    });
    this.driverStats = stats;
  }

  private isApprovedStatus(status: string): boolean {
    const approved = [
      this.sharedService.ApprovedbyManager,
      this.sharedService.ApprovedbyCEO,
      this.sharedService.ApprovedbyAccounts,
      this.sharedService.ApprovedbyRDH,
      this.sharedService.ApprovedbyDeliveryHead,
      this.sharedService.TransfertoBank,
      this.sharedService.ProcessedbyAcocunts
    ];
    return approved.includes(status);
  }

  private isRejectedStatus(status: string): boolean {
    const rejected = [
      this.sharedService.RejectedByManager,
      this.sharedService.RejectedByCEOApprover,
      this.sharedService.RejectedByAccounts,
      this.sharedService.RejectedBySBU,
      this.sharedService.RejectedByRDH,
      this.sharedService.RejectedByDeliveryHead
    ];
    return rejected.includes(status);
  }

  private isPendingStatus(status: string): boolean {
    const pending = [
      this.sharedService.PendingwithManager,
      this.sharedService.PendingwithSBU,
      this.sharedService.PendingwithCEO,
      this.sharedService.PendingwithAccounts,
      this.sharedService.PendingwithRDH,
      this.sharedService.PendingwithDeliveryHead
    ];
    return pending.includes(status);
  }

  loginFromSSO() {
    this.token = this.activatedRoute.snapshot.queryParamMap.get('t');
    if (this.sharedService.checkProperty(this.token)) {
      return;
    }
    this.authService.markSessionActive();
    const formdata = new FormData();
    formdata.append('token', this.token);

    this.authService.userLoginSSO(formdata).subscribe(
      udata => {
        if (udata.Status) {
          const userResult = udata.Data;
          if (userResult.companyId == 'THR') {
            this._router.navigate(['/ecr']);
          } else {
            this._router.navigate(['/dashboard'], { replaceUrl: true, queryParams: {} });
          }
          sessionStorage.setItem('userId', userResult.TMC);
          localStorage.setItem('userId', userResult.TMC);
          sessionStorage.setItem('FullName', userResult.Full_Name);
          sessionStorage.setItem('ReportingHeadId', userResult.Reporting_Head_ID);
          sessionStorage.setItem('ReportingManagerName', userResult.Reporting_Head_Name);
          sessionStorage.setItem('ReportingManagerEmail', userResult.Reporting_Head_Email);
          sessionStorage.setItem('UserEmail', userResult.Company_E_Mail);
          sessionStorage.setItem('UserDesignation', userResult.Designation);
          sessionStorage.setItem('UserDepartment', userResult.Department);
          sessionStorage.setItem('UserSBU', userResult.EmployeeSBU);
          sessionStorage.setItem('UserSUBBU', userResult.SUBBU_Code);
          sessionStorage.setItem('UserSBUCode', userResult.SBUCode);
          sessionStorage.setItem('UserBranch', userResult.Branch);
          sessionStorage.setItem('UserLocation', userResult.Location_Code);
          sessionStorage.setItem('IsHrAdmin', userResult.HR_Admin);
          sessionStorage.setItem('DOJ', userResult.Date_of_Joining);
          sessionStorage.setItem('Band', userResult.Employee_Band);
          sessionStorage.setItem('phoneNo', userResult.Mobile_Phone_No);
          sessionStorage.setItem('Birth_Date', userResult.Birth_Date);
          sessionStorage.setItem('CompanyName', userResult.CompanyName);
          sessionStorage.setItem('IsReEmployee', userResult.IsReEmployee);
          sessionStorage.setItem('companyId', userResult.companyId);
          const isBuhrFlagDash = userResult.ISBUHR === 1 || userResult.ISBUHR === '1' || userResult.ISBUHR === true;
          sessionStorage.setItem('ISBUHR', isBuhrFlagDash ? '1' : '0');
          this.sharedService.setItem('userdata', udata.Data);
          if (userResult.UserRole.filter(x => x.ACCESS_ROLE == 'HRPROFILE').length > 0) {
            sessionStorage.setItem('UserHRRole', userResult.UserRole.filter(x => x.ACCESS_ROLE == this.sharedService.UserRoleHR)[0].ACCESS_ROLE);
          } else {
            sessionStorage.setItem('UserHRRole', '');
          }
          if (userResult.UserRole.filter(x => x.ACCESS_ROLE == 'ACCOUNTS').length > 0) {
            sessionStorage.setItem('UserAccountsRole', userResult.UserRole.filter(x => x.ACCESS_ROLE == this.sharedService.UserRoleAccounts)[0].ACCESS_ROLE);
          } else {
            sessionStorage.setItem('UserAccountsRole', '');
          }
          if (userResult.UserRole.filter(x => x.ACCESS_ROLE == 'BUHEAD').length > 0) {
            sessionStorage.setItem('UserRoleBUHead', userResult.UserRole.filter(x => x.ACCESS_ROLE == this.sharedService.UserRoleBUHead)[0].ACCESS_ROLE);
          } else {
            sessionStorage.setItem('UserRoleBUHead', '');
          }
          if (userResult.UserRole.filter(x => x.ACCESS_ROLE == 'TRAVELDESK').length > 0) {
            sessionStorage.setItem('UserTravelDesk', userResult.UserRole.filter(x => x.ACCESS_ROLE == this.sharedService.UserTravelDesk)[0].ACCESS_ROLE);
          } else {
            sessionStorage.setItem('UserTravelDesk', '');
          }
          if (userResult.UserRole.filter(x => x.ACCESS_ROLE == 'FINANCE').length > 0) {
            sessionStorage.setItem('UserFinanceRole', userResult.UserRole.filter(x => x.ACCESS_ROLE == this.sharedService.UserRoleFinance)[0].ACCESS_ROLE);
          } else {
            sessionStorage.setItem('UserFinanceRole', '');
          }
          if (userResult.UserRole.filter(x => x.ACCESS_ROLE == 'KOCKPIT').length > 0) {
            sessionStorage.setItem('UserKockpitRole', userResult.UserRole.filter(x => x.ACCESS_ROLE == this.sharedService.UserKockpitRole)[0].ACCESS_ROLE);
          } else {
            sessionStorage.setItem('UserKockpitRole', '');
          }

          sessionStorage.setItem('Reportees', userResult.Reportees);
          sessionStorage.setItem('LoginFlag', '1');
          userResult.LoginFlag = 1;
          this.sharedService.setUserData(userResult);
          this.sharedService.UserId = sessionStorage.getItem('userId');
          this.authService.markSessionActive();
          this.EcrModel.TMCNo = sessionStorage.getItem('userId');
          this.EcrModel.EmployeeName = sessionStorage.getItem('FullName');
          this.EcrModel.FromEmail = sessionStorage.getItem('UserEmail');
          this.CompanyName = sessionStorage.getItem('CompanyName');
          this.Reportees = sessionStorage.getItem('Reportees');
          this.Band = sessionStorage.getItem('Band');
          this.userName = sessionStorage.getItem('FullName') || '';
          this.userDesignation = sessionStorage.getItem('UserDesignation') || '';
          this.tmc = sessionStorage.getItem('userId');
          this.loadDashboardSummary();
        } else {
          alert(udata.Message);
          this._router.navigate(['/']);
        }
      },
      error => {
        console.log(error);
      }
    );
  }

  deleteAllCookie() {
    location.reload();
  }
}
